/**
 * SystemStatusPanel — small "operations" tile that surfaces the
 * health of the data plane: API status, audit-chain length, agent
 * count, last sync wall-clock. Mounted at the bottom of CAIO and CEO
 * dashboards so an operator can glance at it without leaving the page.
 *
 * Data comes from the aggregate `/api/dashboard` payload's
 * `systemStatus` block, which is computed server-side at request time.
 */
import React from "react";
import { Activity, ShieldCheck, Server, Clock } from "lucide-react";
import { PanelCard } from "@/pages/dashboard/components/DashboardPrimitives";
import { useDashboardData } from "@/contexts/DashboardDataContext";

function fmtCheckedAt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.valueOf())) return iso;
  return d.toLocaleString("en-AU", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    day: "2-digit",
    month: "short",
  });
}

export function SystemStatusPanel() {
  const { systemStatus } = useDashboardData();
  if (!systemStatus) return null;

  const rows = [
    {
      key: "api",
      label: "API",
      value: systemStatus.api === "ok" ? "Healthy" : systemStatus.api,
      icon: Server,
      tone: systemStatus.api === "ok" ? "text-risk-low" : "text-risk-high",
      testId: "system-status-api",
    },
    {
      key: "audit",
      label: "Audit chain",
      value: `${systemStatus.auditEntries} entries`,
      icon: ShieldCheck,
      tone: "text-tralenex-700",
      testId: "system-status-audit",
    },
    {
      key: "agents",
      label: "Governed agents",
      value: `${systemStatus.agentsGoverned} of ${systemStatus.agentsTotal}`,
      icon: Activity,
      tone: "text-ink-primary",
      testId: "system-status-agents",
    },
    {
      key: "checked",
      label: "Checked at",
      value: fmtCheckedAt(systemStatus.checkedAt),
      icon: Clock,
      tone: "text-ink-secondary",
      testId: "system-status-checked",
    },
  ];

  return (
    <PanelCard title="System status" testId="system-status-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {rows.map((r) => {
          const Icon = r.icon;
          return (
            <div
              key={r.key}
              className="flex items-start gap-2"
              data-testid={r.testId}
            >
              <Icon size={14} className={`mt-0.5 shrink-0 ${r.tone}`} />
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.08em] text-ink-secondary font-semibold">
                  {r.label}
                </p>
                <p className={`text-sm font-semibold tabular-nums ${r.tone}`}>
                  {r.value}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </PanelCard>
  );
}
